import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  TextArea,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  UncontrolledTooltip,
  Table
} from "reactstrap";
import { Formik, useFormik, ErrorMessage, Form, Field, FieldArray } from "formik";
import * as Yup from "yup";
import { Link } from "react-router-dom";
import { FiXCircle } from "react-icons/fi";
import { FiTrash2 } from "react-icons/fi";
import CustomSelect from "components/Common/custom-select";
import Breadcrumbs from "components/Common/Breadcrumb";
import TableContainer from "components/Common/TableContainer";
import getAccessRights from "components/Common/getAccessRights";
import { useLocation, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { fetchActiveEmployees, fetchFixedDeduction, saveFixedDeduction, fetchDeduction, removeFixedDeduction, getMonthlyDeductionTemplate, deductionlistImport, resetAll } from "store/actions";
import { showToast, showConfirm } from "../../../../common/Toaster";
import Loader from "common/pageLoader";

const FixedDeductions = () => {
  const formikRef = useRef();

  const [fixedDeductionDataDefault, setFixedDeductionDataDefault] = useState({ emp_id: "", emp_code: "", emp_name: "", amount: "", ded_id: "0", monthly_ded_id: "", emp_no: '', remarks: "", enable_status: false, entry_type: 2 });
  const [fixedDeductionData, setFixedDeductionData] = useState([]);
  const [deleteData, setDeleteData] = useState([]);

  const [searchStatus, setSearchStatus] = useState(false);
  const [saveStatus, setSaveStatus] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);
  const {
    fixedDeduction,
    deductionList,
    employees,
    template,
    importedData,
    loading,
    success,
    error,
  } = useSelector((state) => ({
    fixedDeduction: state.PayrollDeductionFixedDeductionReducer?.fixedDeduction ?? [],
    deductionList: state.PayrollDeductionFixedDeductionReducer?.deductionList ?? [],
    employees: state.EmployeeDetailsReducer?.employeesActiveList ?? [],
    template: state.PayrollDeductionFixedDeductionReducer?.template,
    importedData: state.PayrollDeductionFixedDeductionReducer?.importedData,
    loading: state.PayrollDeductionFixedDeductionReducer?.loading,
    error: state.PayrollDeductionFixedDeductionReducer?.error,
    success: state.PayrollDeductionFixedDeductionReducer?.success,
  }));

  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deductionsData, setDeductionsData] = useState(null);
  const [employeeData, setEmployeeData] = useState(employees);
  const [fetchFixedDeductionStatus, setFetchFixedDeductionStatus] = useState(false);

  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    const accessRight = getAccessRights(location.pathname, history);
    setFixedDeductionData([]);
  }, [location]);

  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0])
    const unitId = authUserDetails[0].unit_id
    dispatch(fetchActiveEmployees(unitId))
    dispatch(fetchDeduction(unitId, 3))
  }, [dispatch]);

  useEffect(() => {
    if (success) {

      if (fetchFixedDeductionStatus) {
        const formData = new FormData();
        formData.append("pay_dedn_id", deductionsData.value);
        formData.append("create_by", authDetails?.user_id);
        formData.append("unit_id", authDetails?.unit_id);
        //dispatch(fetchDeduction(formData));
        dispatch(fetchFixedDeduction(formData));
      }
      setFetchFixedDeductionStatus(false);

      if (fixedDeduction?.length > 0) {
        //Remove the selected employees from the list
        resetEmployeeData(fixedDeduction);

        setFixedDeductionData([...fixedDeduction, fixedDeductionDataDefault]);
      } else {
        setFixedDeductionData([fixedDeductionDataDefault]);
        resetEmployeeData([fixedDeductionDataDefault]);
      }
      setIsEdit(false);
      setEditId(null);
    } else {
      setFixedDeductionData([fixedDeductionDataDefault]);
    }

  }, [authDetails, success, dispatch]);

  const initialDCancelRemarksValues = {
    remarks: ""
  };

  const validationDCancelRemarksSchema = Yup.object({
    remarks: Yup.string().required("Remarks is required"),
  });

  const initialValues = { deductions: "" };
  const validationSchema = Yup.object({ deductions: Yup.object().nullable().required("Deductions is required"), });

  const deductionSchema = Yup.object().shape({
    emp_id: '',
    emp_code: '',
    emp_no: Yup.object().test('emp_no', 'Employee Number is required', function (value) {
      const { path, parent, options } = this;
      const index = parseInt(path.match(/\d+/)?.[0] ?? -1);
      //const lastIndex = options.context?.length - 1;
      const lastIndex = fixedDeductionData?.length - 1;

      const isLastRow = index === lastIndex;
      const hasAnyValue = !!parent.emp_no || !!parent.emp_name || !!parent.amount || !!parent.remarks;

      if (isLastRow && !hasAnyValue) { return true; }
      return !!value; // ✅ validate if value is expected
    }).nullable(),

    emp_name: Yup.string().test('emp_name', 'Name is required', function (value) {
      const { path, parent, options } = this;
      const index = parseInt(path.match(/\d+/)?.[0] ?? -1);
      //const lastIndex = options.context?.length - 1;
      const lastIndex = fixedDeductionData?.length - 1;

      const isLastRow = index === lastIndex;
      const hasAnyValue = !!parent.emp_no || !!parent.emp_name || !!parent.amount || !!parent.remarks;

      if (isLastRow && !hasAnyValue) { return true; }
      return !!value; // ✅ validate if value is expected
    }),

    amount: Yup.number().test('amount', 'Amount is required', function (value) {
      const { path, parent, options } = this;
      const index = parseInt(path.match(/\d+/)?.[0] ?? -1);
      //const lastIndex = options.context?.length - 1;
      const lastIndex = fixedDeductionData?.length - 1;

      const isLastRow = index === lastIndex;
      const hasAnyValue = !!parent.emp_no || !!parent.emp_name || !!parent.amount || !!parent.remarks;

      if (isLastRow && !hasAnyValue) { return true; }
      return value !== undefined && value !== ''; // ✅ validate if filled
    }),

    remarks: Yup.string().test('remarks', 'Remarks is required', function (value) {
      const { path, parent, options } = this;
      const index = parseInt(path.match(/\d+/)?.[0] ?? -1);
      //const lastIndex = options.context?.length - 1;
      const lastIndex = fixedDeductionData?.length - 1;

      const isLastRow = index === lastIndex;
      const hasAnyValue = !!parent.emp_no || !!parent.emp_name || !!parent.amount || !!parent.remarks;

      if (isLastRow && !hasAnyValue) {
        return true; // ✅ skip validation if empty
      }

      return !!value; // ✅ validate if value is expected
    }),
  });

  const validationDeductionSchema = Yup.object().shape({ deduction: Yup.array().of(deductionSchema) });

  const handleSearch = async (formik) => {
    await formik.validateField('deductions');
    formik.setFieldTouched('deductions', true);
    setFixedDeductionData([]);

    if (formik?.values?.deductions?.value) {
      const formData = new FormData();
      formData.append("pay_dedn_id", formik.values.deductions.value);
      formData.append("create_by", authDetails?.user_id);
      formData.append("unit_id", authDetails?.unit_id);
      dispatch(fetchFixedDeduction(formData));
      setSearchStatus(true);
    }
  };

  const handleDelete = (ded, index) => {
    if (ded?.entry_type === 2) {
      const newFixedDedData = removeByIndex(fixedDeductionData, index); // Removes object at index
      setFixedDeductionData(newFixedDedData);
      //Remove the selected employees from the list
      resetEmployeeData(newFixedDedData);
    } else {
      setDeleteData(ded);
      setIsOpen(true);
    }
  };

  const handleNextRow = (index) => {
    if (formikRef.current) {
      formikRef.current[index]?.focus();

      formikRef.current.validateForm().then((errors) => {
        //console.log('Object.keys(errors).length',Object.keys(errors).length);

        formikRef.current.handleSubmit();

        /*
        const touchedFields = {};
        touchedFields[`deduction[${index}].emp_no`] = true;
        touchedFields[`deduction[${index}].emp_name`] = true;
        touchedFields[`deduction[${index}].amount`] = true;
        touchedFields[`deduction[${index}].remarks`] = true;          
        formikRef.current.setTouched(touchedFields);
        */

        //console.log('formikRef.current.values',formikRef.current.values);
        //console.log('formikRef.current.values.deduction',formikRef.current.values.deduction[index]);      
        if (!errors.deduction) {
          const findEmptyDeduction = formikRef.current.values.deduction.filter(emp => emp.emp_no === '' || emp.emp_name === '' || emp.amount === '' || emp.remarks === '');
          if (findEmptyDeduction.length <= 0) {
            let fixedDedData = [...formikRef.current.values.deduction, ...[fixedDeductionDataDefault]];
            fixedDedData.forEach(function (element) {
              if (element?.emp_no) {
                element.enable_status = true;
                element.emp_no = { value: element.emp_id, label: element.emp_no + ' - ' + element.emp_name };
                //element.ded_id = element.monthly_ded_id;
                element.ded_id = 0;
              }
            });
            setFixedDeductionData(fixedDedData);

            //Remove the selected employees from the list
            resetEmployeeData(fixedDedData);

          }
        }
      });

    }
  }

  const handleSave = () => {
    //formik.handleSubmit();
    if (formikRef.current) {
      formikRef.current.validateForm().then((errors) => {
        formikRef.current.handleSubmit();
        if (!errors.deduction) {
          let submitData = formikRef.current.values.deduction;
          submitData = submitData.filter(function (obj) {
            return obj.emp_name !== '';
          });

          const newFixedDeductionCount = submitData.filter(entry => entry.entry_type === 2).length;
          if (newFixedDeductionCount > 0) {
            const keysToRemove = ['emp_name', 'emp_no', 'enable_status', 'entry_type', 'monthly_ded_id'];

            submitData = submitData.map(obj => {
              const newObj = { ...obj };
              keysToRemove.forEach(key => delete newObj[key]);
              return newObj;
            });

            let lst_employee_deduction_data = { ded: submitData };

            const formData = new FormData();
            formData.append("lst_employee_deduction", JSON.stringify(lst_employee_deduction_data));
            formData.append("pay_ded_id", deductionsData?.value);
            formData.append("create_by", authDetails?.user_id);
            dispatch(saveFixedDeduction(formData));
            setFetchFixedDeductionStatus(true);
          }
        }
      })
    }
  };

  const removeByIndex = (array, indexToRemove) => {
    return array.filter((_, index) => index !== indexToRemove);
  };

  const resetEmployeeData = (fixedDeduction) => {
    const withoutSelectedEmployees = employees.filter(function (item) {
      return !fixedDeduction.some(function (fd) {
        return fd.emp_id == item.value;
      });
    });
    setEmployeeData(withoutSelectedEmployees);
  };

  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const handleFileChange = (e) => { setSelectedFile(e.target.files[0]); };

  const handleGetTemplate = () => {
    if (!deductionsData) {
      showToast({ icon: "error", title: "No File selected", text: "Please select Deduction Types first and then proceed" });
      return;
    }
    // Prepare FormData
    const formData = new FormData();
    formData.append("paydedn_id", deductionsData?.value); // pass month here
    formData.append("unit_id", authDetails?.unit_id);
    formData.append("create_by", authDetails?.user_id);

    // Dispatch Redux action
    dispatch(getMonthlyDeductionTemplate(formData));
  };

  const handleImport = () => {
    if (!selectedFile) {
      showToast({ icon: "error", title: "error", text: "Please select a file first!" });
      return;
    }

    const formData = new FormData();
    formData.append("unit_id", authDetails?.unit_id);
    formData.append("create_by", authDetails?.user_id);
    formData.append("paydedn_id", deductionsData?.value); // pass month here
    formData.append("ded_file", selectedFile);
    dispatch(deductionlistImport(formData))
  };

  useEffect(() => {
    if (!success) {
      const formData = new FormData();
      formData.append("pay_dedn_id", deductionsData?.value);
      formData.append("create_by", authDetails?.user_id);
      formData.append("unit_id", authDetails?.unit_id);
      dispatch(fetchFixedDeduction(formData));
      setSearchStatus(true);
    }
  }, [authDetails, success, dispatch]);

  return (
    <div className="page-content">
      <Loader loading={loading} />
      <Container fluid>
        <Breadcrumbs title="Payroll / Deduction" breadcrumbItem="Fixed Deductions" />
        <Card>
          <CardBody>

            <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
              <ModalHeader tag="h4">
                Remove Reason
              </ModalHeader>
              <ModalBody>
                <Formik
                  initialValues={initialDCancelRemarksValues}
                  validationSchema={validationDCancelRemarksSchema}
                  onSubmit={(values) => {
                    const formData = new FormData();
                    formData.append("pay_ded_id", deleteData?.monthly_ded_id);
                    formData.append("remarks", values?.remarks);
                    formData.append("create_by", authDetails?.user_id);
                    dispatch(removeFixedDeduction(formData));
                    setDeleteData();
                    setIsOpen(false);
                    setFetchFixedDeductionStatus(true);
                  }}>
                  {(formik) => (
                    <Form>
                      <Row form>
                        <Col xs={12}>
                          <div className="mb-3">
                            <Label className="form-label">Remarks<span className="text-danger">*</span></Label>
                            <Field as="textarea" name="remarks" className="form-control" />
                            <ErrorMessage name="remarks" component="div" className="text-danger" />
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <div className="text-end">
                            <button type="submit" className="me-2 btn btn-primary">Save</button>
                            {/* <button type="button" className="btn btn-secondary" onClick={() => { setIsOpen(false); setDeleteData(); }}>Cancel</button> */}
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => {
                                showConfirm({
                                  title: "Are you sure?", text: "Do you want to Cancel?",
                                  confirmText: "Yes, Cancel", cancelText: "No", onConfirm: () => {
                                    setIsOpen(false);
                                    setDeleteData();
                                  }
                                });
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </Col>
                      </Row>
                    </Form>
                  )}
                </Formik>
              </ModalBody>
            </Modal>


            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={(values) => {
                //console.log('Submitted',values);
              }}>
              {(formik) => (
                <Form>
                  <Row>
                    <Col md={3}>
                      <FormGroup>
                        <Label for="deductions">Deductions <span className="text-danger">*</span></Label>
                        <CustomSelect
                          id="deductions"
                          name="deductions"
                          options={deductionList}
                          value={deductionsData}
                          onChange={(selectedOption) => { formik.setFieldValue("deductions", selectedOption); setFixedDeductionData([]); setDeductionsData(selectedOption); setSearchStatus(false); }}
                          onBlur={() => formik.setFieldTouched("deductions", true)}
                        />
                        {formik.touched.deductions && formik.errors.deductions && (
                          <p className="text-danger ">{formik.errors.deductions}</p>
                        )}
                      </FormGroup>
                    </Col>
                    <Col className="d-flex align-items-center mt-3">
                      <Button type="submit" className="btn button-search me-2" onClick={() => handleSearch(formik)}><i className="fas fa-search me-1" /> Search</Button>
                      {searchStatus && (
                        <>
                          <Button type="button" className="btn button-save me-2" onClick={handleSave}><i className="fas fa-save me-1" /> Save</Button>
                          <>
                            {deductionsData && (
                              <Button type="button" className="btn button-export me-2" onClick={handleGetTemplate} style={{ whiteSpace: "nowrap", height: "36px", minWidth: "140px" }}>
                                <i className="fas fa-download me-1" /> Get Template
                              </Button>
                            )}
                          </>

                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => {
                              showConfirm({
                                title: "Are you sure?", text: "Do you want to Cancel?",
                                confirmText: "Yes, Cancel", cancelText: "No", onConfirm: () => {
                                  formik.resetForm();
                                  formik.setFieldValue("deductions", null);
                                  setFixedDeductionData([]);
                                  setDeductionsData(null);
                                  setSearchStatus(false);
                                  setSelectedFile(null);
                                  if (fileInputRef.current) { fileInputRef.current.value = ""; }
                                }
                              });
                            }}
                          >
                            <i className="fas fa-sync me-1" /> Cancel
                          </button>
                          <>
                            {selectedFile && (
                              <Button type="button" className="btn button-export" onClick={handleImport}>
                                <i className="fas fa-file-import me-1" />  Import
                              </Button>
                            )}
                          </>

                          <Label for="file" className="p-3">File <span className="text-danger">*</span></Label>
                          <div className="col-md-3">
                            <Input
                              id="file"
                              name="file"
                              type="file"
                              accept=".xls,.xlsx"
                              ref={fileInputRef}
                              onChange={handleFileChange}
                              onBlur={formik.handleBlur}
                              className={`form-control ${formik.touched.file && formik.errors.file ? "is-invalid" : ""
                                }`}
                            />
                            {formik.touched.file && formik.errors.file && (
                              <div className="invalid-feedback d-block">
                                {formik.errors.file}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </Col>
                  </Row>
                </Form>
              )}
            </Formik>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Formik
              initialValues={{ deduction: fixedDeductionData }}
              enableReinitialize
              innerRef={formikRef}
              validationSchema={validationDeductionSchema}
              //context={{ length: fixedDeductionData?.length ? fixedDeductionData?.length:0 }} // Required for row logic
              validateOnBlur={true}
              validateOnChange={true}
              onSubmit={(values) => {
                //console.log('Submitted',values);
              }}>
              {({ values, setFieldValue, setFieldTouched, validateField, errors, touched }) => (
                <Form>
                  <div className="table-responsive overflow-visible">
                    <Table bordered>
                      <thead className="thead-dark">
                        <tr>
                          <th className="text-center">Action</th>
                          <th>Emp Code<span className="text-danger ms-1">*</span></th>
                          <th>Name</th>
                          <th>Amount<span className="text-danger ms-1">*</span></th>
                          <th>Remarks<span className="text-danger ms-1">*</span></th>
                        </tr>
                      </thead>
                      <tbody>
                        <FieldArray name="deduction">
                          {() => (
                            typeof deductionsData === 'object' && deductionsData !== null && values?.deduction?.length > 0 ? (
                              values.deduction.map((ded, index) => (
                                <tr key={index}>
                                  <td className="text-center">
                                    {(fixedDeductionData?.length > 1 && values?.deduction?.length - 1 !== index) && (
                                      <FiTrash2 size={20} color="red" style={{ cursor: "pointer" }} onClick={() => handleDelete(ded, index)} />)}
                                  </td>
                                  <td>
                                    {
                                      ded.enable_status ?
                                        <>
                                          <Input name={`deduction[${index}].emp_code`} disabled value={ded.emp_code} />
                                          <Input type="hidden" name={`deduction[${index}].emp_no`} disabled value={ded.emp_no} />
                                        </>
                                        :
                                        <>
                                          <CustomSelect
                                            options={employeeData}
                                            isDisabled={ded.enable_status}
                                            name={`deduction[${index}].emp_no`}
                                            value={employeeData.find((option) => option.value == ded.emp_id)} // Store full object
                                            onChange={(selectedOption) => {
                                              if (!selectedOption) {
                                                setFieldValue(`deduction[${index}].emp_no`, null);
                                                setFieldValue(`deduction[${index}].emp_id`, "");
                                                setFieldValue(`deduction[${index}].emp_code`, "");
                                                setFieldValue(`deduction[${index}].emp_name`, "");
                                                return;
                                              }
                                              const [emp_code, emp_name] = selectedOption.label.split(" - ");

                                              setFieldValue(`deduction[${index}].emp_no`, selectedOption);
                                              setFieldTouched(`deduction[${index}].emp_no`, true, true);
                                              validateField(`deduction[${index}].emp_no`);

                                              setFieldValue(`deduction[${index}].emp_id`, selectedOption.value);
                                              setFieldTouched(`deduction[${index}].emp_id`, true, true);

                                              setFieldValue(`deduction[${index}].emp_code`, emp_code);
                                              setFieldTouched(`deduction[${index}].emp_code`, true, true);

                                              setFieldValue(`deduction[${index}].emp_name`, emp_name);
                                              setFieldTouched(`deduction[${index}].emp_name`, true, true);
                                              validateField(`deduction[${index}].emp_name`);
                                            }
                                            }

                                            onBlur={() => { setFieldTouched(`deduction[${index}].emp_name`, true); setFieldTouched(`deduction[${index}].emp_no`, true); handleNextRow(index); }}
                                          />
                                          {
                                            /*
                                            onChange={(selectedOption) => { 
                                                console.log(selectedOption);
                                                let selectedEmpArray = selectedOption.label.split(" - ");
                                                if(selectedEmpArray?.length>1)
                                                {
                                                  setFieldValue(`deduction[${index}].emp_name`, selectedEmpArray[1]);
                                                  setFieldTouched(`deduction[${index}].emp_name`, true, true);
                                                  validateField(`deduction[${index}].emp_name`);
        
                                                  setFieldValue(`deduction[${index}].emp_id`, selectedOption.value);
                                                  setFieldTouched(`deduction[${index}].emp_id`, true, true);
                                                  //validateField(`deduction[${index}].emp_id`);
        
                                                  setFieldValue(`deduction[${index}].emp_code`, selectedEmpArray[0]);
                                                  setFieldTouched(`deduction[${index}].emp_code`, true, true);
                                                  
                                                }                                      
                                                setFieldValue(`deduction[${index}].emp_no`, selectedOption);
                                                setFieldTouched(`deduction[${index}].emp_no`, true, true);
                                                validateField(`deduction[${index}].emp_no`);
        
                                                setFieldTouched(`deduction[${index}].amount`, true, true);
                                                validateField(`deduction[${index}].amount`);
                                                validateField(`deduction[${index}].remarks`);                                        
                                              }
                                            
                                            */
                                          }
                                          <ErrorMessage name={`deduction[${index}].emp_no`} component="div" className="error" />
                                        </>
                                    }
                                  </td>
                                  <td>
                                    <Input name={`deduction[${index}].emp_name`} disabled value={ded.emp_name} onChange={(e) => { handleNextRow(index); }} onBlur={(e) => { handleNextRow(index); }} />
                                    <ErrorMessage name={`deduction[${index}].emp_name`} component="div" className="error" />
                                  </td>
                                  <td>
                                    <Input name={`deduction[${index}].amount`} type="number" className="text-end1" disabled={ded.enable_status} value={ded.amount}
                                      onKeyDown={(e) => {
                                        if (["-", "+", "*", "/", "e", "E", "."].includes(e.key)) {
                                          e.preventDefault();
                                        }
                                      }}
                                      onInput={(e) => {
                                        const value = e.target.value.replace(/[^0-9]/g, "");
                                        setFieldValue(`deduction[${index}].amount`, value);
                                      }}
                                      // onInput={
                                      //   (e) => {
                                      //     setFieldValue(`deduction[${index}].amount`, e.target.value);
                                      //     //handleNextRow(index);
                                      //   }
                                      // }
                                      onBlur={(e) => { handleNextRow(index); }}
                                    />
                                    <ErrorMessage name={`deduction[${index}].amount`} component="div" className="error" />
                                  </td>
                                  <td>
                                    <Input name={`deduction[${index}].remarks`} value={ded.remarks} className="text-end1" disabled={ded.enable_status} onInput={
                                      (e) => {
                                        setFieldValue(`deduction[${index}].remarks`, e.target.value);
                                        //handleNextRow(index);                                    
                                      }
                                    }
                                      onBlur={(e) => { handleNextRow(index); }}
                                    />
                                    <ErrorMessage name={`deduction[${index}].remarks`} component="div" className="error" />
                                  </td>
                                </tr>
                              ))
                            ) : '')
                          }
                        </FieldArray>
                      </tbody>
                    </Table>
                  </div>
                </Form>
              )}
            </Formik>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default FixedDeductions;
